/**
 * Admin Service
 * API calls for admin dashboard functionality
 */

import { getApiUrl, TOKEN_STORAGE_KEY } from '../config/app.config';

const API_URL = getApiUrl();

// Helper to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

// Helper for API requests
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAuthToken();

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

// ============================================================================
// USER MANAGEMENT
// ============================================================================

export interface GetUsersParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  role?: string;
  isBanned?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const getUsers = async (params: GetUsersParams = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  });

  return apiRequest(`/admin/users?${queryParams.toString()}`);
};

const getUserDetails = async (userId: string) => {
  return apiRequest(`/admin/users/${userId}`);
};

const updateUser = async (userId: string, data: any) => {
  return apiRequest(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

const deleteUser = async (userId: string) => {
  return apiRequest(`/admin/users/${userId}`, {
    method: 'DELETE',
  });
};

const banUser = async (userId: string, data: { reason: string; duration?: number }) => {
  return apiRequest(`/admin/users/${userId}/ban`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

const unbanUser = async (userId: string) => {
  return apiRequest(`/admin/users/${userId}/unban`, {
    method: 'POST',
  });
};

const updateUserChips = async (userId: string, data: { amount: number; operation: 'add' | 'subtract' | 'set' }) => {
  return apiRequest(`/admin/users/${userId}/chips`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

// ============================================================================
// TABLE MANAGEMENT
// ============================================================================

export interface GetTablesParams {
  page?: number;
  limit?: number;
}

const getTables = async (params: GetTablesParams = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, value.toString());
    }
  });

  return apiRequest(`/admin/tables?${queryParams.toString()}`);
};

const getTableDetails = async (tableId: string) => {
  return apiRequest(`/admin/tables/${tableId}`);
};

const closeTable = async (tableId: string) => {
  return apiRequest(`/admin/tables/${tableId}`, {
    method: 'DELETE',
  });
};

const kickPlayerFromTable = async (tableId: string, data: { playerId: string; reason?: string }) => {
  return apiRequest(`/admin/tables/${tableId}/kick-player`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// ============================================================================
// GAME MANAGEMENT
// ============================================================================

export interface GetGamesParams {
  page?: number;
  limit?: number;
  tableId?: string;
  startDate?: string;
  endDate?: string;
}

const getGames = async (params: GetGamesParams = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, value.toString());
    }
  });

  return apiRequest(`/admin/games?${queryParams.toString()}`);
};

const getGameDetails = async (gameId: string) => {
  return apiRequest(`/admin/games/${gameId}`);
};

const getGameStatistics = async () => {
  return apiRequest('/admin/games/statistics');
};

// ============================================================================
// BOT MANAGEMENT
// ============================================================================

export interface GetBotsParams {
  page?: number;
  limit?: number;
}

export interface CreateBotData {
  name: string;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD' | 'EXPERT';
  aggressiveness?: number;
  bluffFrequency?: number;
  tightness?: number;
  avatar?: string;
  description?: string;
}

export interface UpdateBotData {
  name?: string;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD' | 'EXPERT';
  aggressiveness?: number;
  bluffFrequency?: number;
  tightness?: number;
  avatar?: string;
  description?: string;
}

export interface DeployBotData {
  tableId?: string;
  autoJoin?: boolean;
}

const getBots = async (params: GetBotsParams = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, value.toString());
    }
  });

  return apiRequest(`/admin/bots?${queryParams.toString()}`);
};

/**
 * Only the fields the server's bot DTOs accept. Its validation rejects any
 * unknown property outright (forbidNonWhitelisted), so passing a wider form
 * object straight through would fail the whole request.
 */
const BOT_FIELDS = ['name', 'difficulty', 'aggressiveness', 'bluffFrequency', 'tightness', 'avatar', 'description'] as const;

const pickBotFields = (data: Record<string, unknown>) =>
  Object.fromEntries(BOT_FIELDS.filter((key) => data[key] !== undefined).map((key) => [key, data[key]]));

const createBot = async (data: CreateBotData) => {
  return apiRequest('/admin/bots', {
    method: 'POST',
    body: JSON.stringify(pickBotFields({ ...data })),
  });
};

const updateBot = async (botId: string, data: UpdateBotData) => {
  return apiRequest(`/admin/bots/${botId}`, {
    method: 'PUT',
    body: JSON.stringify(pickBotFields({ ...data })),
  });
};

/** Bots have no active/inactive flag on the server yet (BotPlayer has no such column). */
const setBotActive = async (_botId: string, _active: boolean): Promise<never> => {
  throw new Error('Activating/deactivating bots is not supported by the server yet');
};

const deleteBot = async (botId: string) => {
  return apiRequest(`/admin/bots/${botId}`, {
    method: 'DELETE',
  });
};

const deployBot = async (botId: string, data: DeployBotData) => {
  return apiRequest(`/admin/bots/${botId}/deploy`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// ============================================================================
// SYSTEM STATISTICS
// ============================================================================

const getDashboardOverview = async () => {
  return apiRequest('/admin/stats/overview');
};

const getRevenueAnalytics = async () => {
  return apiRequest('/admin/stats/revenue');
};

const getActivityMetrics = async () => {
  return apiRequest('/admin/stats/activity');
};

// ============================================================================
// EXPORTS
// ============================================================================

export const adminService = {
  // Users
  getUsers,
  getUserDetails,
  updateUser,
  deleteUser,
  banUser,
  unbanUser,
  updateUserChips,

  // Tables
  getTables,
  getTableDetails,
  closeTable,
  kickPlayerFromTable,

  // Games
  getGames,
  getGameDetails,
  getGameStatistics,

  // Bots
  getBots,
  createBot,
  updateBot,
  setBotActive,
  deleteBot,
  deployBot,

  // Stats
  getDashboardOverview,
  getRevenueAnalytics,
  getActivityMetrics,
};
